import { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { AuthContext } from '@/App';
import axios from 'axios';
import { Phone, PhoneOff, Mic, MicOff, Video, VideoOff, X } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
  ]
};

const IncomingCallListener = () => {
  const { user, token } = useContext(AuthContext);
  const [phase, setPhase] = useState('idle'); // idle | ringing | in-call
  const [incomingCall, setIncomingCall] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [callDuration, setCallDuration] = useState(0);

  const ringtoneRef = useRef(null);
  const pollingRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const durationTimerRef = useRef(null);
  const icePollingRef = useRef(null);
  const dismissedCallsRef = useRef(new Set());
  const ringingPollRef = useRef(null);
  const processedIceRef = useRef(0);
  const ringingTimeoutRef = useRef(null);

  // Attach streams to elements once phase is 'in-call' and refs are mounted
  useEffect(() => {
    if (phase === 'in-call') {
      if (localVideoRef.current && localStreamRef.current) {
        localVideoRef.current.srcObject = localStreamRef.current;
      }
      if (remoteVideoRef.current && remoteStreamRef.current) {
        remoteVideoRef.current.srcObject = remoteStreamRef.current;
      }
    }
  }, [phase]);

  // Poll for incoming calls every 3s (only in idle phase)
  const pollIncoming = useCallback(async () => {
    if (!token || phase !== 'idle') return;
    try {
      const res = await axios.get(`${API}/calls/incoming`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.length > 0) {
        const call = res.data[0];
        const callAge = (Date.now() - new Date(call.created_at).getTime()) / 1000;
        if (callAge < 45 && !dismissedCallsRef.current.has(call.call_id)) {
          setIncomingCall(call);
          setPhase('ringing');
          playRingtone();
        }
      }
    } catch (err) { console.debug('Call listener error:', err.message); }
  }, [token, phase]);

  useEffect(() => {
    if (user && token && phase === 'idle') {
      pollIncoming();
      pollingRef.current = setInterval(pollIncoming, 3000);
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [user, token, phase, pollIncoming]);

  // Poll call status during ringing to detect caller hang-up + auto-dismiss after 45s
  useEffect(() => {
    if (phase === 'ringing' && incomingCall) {
      ringingPollRef.current = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/calls/${incomingCall.call_id}/status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.data.status === 'ended' || res.data.status === 'declined') {
            stopRingtone();
            dismissedCallsRef.current.add(incomingCall.call_id);
            setPhase('idle');
            setIncomingCall(null);
          }
        } catch (err) { console.debug('Call listener error:', err.message); }
      }, 2000);

      ringingTimeoutRef.current = setTimeout(() => {
        stopRingtone();
        dismissedCallsRef.current.add(incomingCall.call_id);
        setPhase('idle');
        setIncomingCall(null);
      }, 45000);
    }
    return () => {
      if (ringingPollRef.current) clearInterval(ringingPollRef.current);
      if (ringingTimeoutRef.current) clearTimeout(ringingTimeoutRef.current);
    };
  }, [phase, incomingCall, token]);

  // Call duration timer
  useEffect(() => {
    if (phase === 'in-call') {
      durationTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (durationTimerRef.current) clearInterval(durationTimerRef.current);
    };
  }, [phase]);

  const playRingtone = () => {
    try {
      if (!ringtoneRef.current) {
        ringtoneRef.current = new Audio('/audio/ringtone.wav');
        ringtoneRef.current.loop = true;
      }
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current.play().catch((e) => console.debug('Audio play blocked:', e.message));
    } catch (e) { console.debug('Call listener error:', e.message); }
  };

  const stopRingtone = () => {
    try {
      if (ringtoneRef.current) {
        ringtoneRef.current.pause();
        ringtoneRef.current.currentTime = 0;
      }
    } catch (e) { console.debug('Call listener error:', e.message); }
  };

  const cleanupCall = () => {
    stopRingtone();
    if (icePollingRef.current) clearInterval(icePollingRef.current);
    if (durationTimerRef.current) clearInterval(durationTimerRef.current);
    if (ringingPollRef.current) clearInterval(ringingPollRef.current);
    if (ringingTimeoutRef.current) clearTimeout(ringingTimeoutRef.current);
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    remoteStreamRef.current = null;
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    processedIceRef.current = 0;
    setPhase('idle');
    setIncomingCall(null);
    setCallDuration(0);
    setIsMuted(false);
    setIsVideoOff(false);
  };

  const handleAccept = async () => {
    if (!incomingCall) return;
    stopRingtone();
    if (ringingPollRef.current) clearInterval(ringingPollRef.current);
    if (ringingTimeoutRef.current) clearTimeout(ringingTimeoutRef.current);
    dismissedCallsRef.current.add(incomingCall.call_id);

    try {
      const isVideo = incomingCall.call_type === 'video';
      const stream = await navigator.mediaDevices.getUserMedia({
        video: isVideo,
        audio: true
      });
      localStreamRef.current = stream;
      setIsVideoOff(!isVideo);

      const pc = new RTCPeerConnection(ICE_SERVERS);
      pcRef.current = pc;

      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      // Store remote stream in ref — will be attached in useEffect when phase='in-call'
      pc.ontrack = (event) => {
        if (event.streams[0]) {
          remoteStreamRef.current = event.streams[0];
          // Also try attaching directly if refs are already mounted
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = event.streams[0];
          }
        }
      };

      pc.onicecandidate = async (event) => {
        if (event.candidate) {
          try {
            await axios.post(`${API}/calls/${incomingCall.call_id}/ice-candidate`, {
              candidate: JSON.stringify(event.candidate)
            }, { headers: { Authorization: `Bearer ${token}` } });
          } catch (err) { console.debug('Call listener error:', err.message); }
        }
      };

      pc.oniceconnectionstatechange = () => {
        if (pc.iceConnectionState === 'failed') {
          endCall();
        }
      };

      const offer = JSON.parse(incomingCall.offer);
      await pc.setRemoteDescription(new RTCSessionDescription(offer));

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      await axios.post(`${API}/calls/${incomingCall.call_id}/answer`, {
        answer: JSON.stringify(answer)
      }, { headers: { Authorization: `Bearer ${token}` } });

      // Switch to in-call — useEffect will attach streams to mounted refs
      processedIceRef.current = 0;
      setPhase('in-call');

      // Poll for caller's ICE candidates + detect hang-up
      icePollingRef.current = setInterval(async () => {
        try {
          const status = await axios.get(`${API}/calls/${incomingCall.call_id}/status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (status.data.status === 'ended') {
            cleanupCall();
            return;
          }
          if (status.data.ice_candidates) {
            const newCandidates = status.data.ice_candidates.slice(processedIceRef.current);
            for (const c of newCandidates) {
              try {
                await pc.addIceCandidate(new RTCIceCandidate(JSON.parse(c)));
              } catch (e) { console.debug('Call listener error:', e.message); }
            }
            processedIceRef.current = status.data.ice_candidates.length;
          }
        } catch (err) { console.debug('Call listener error:', err.message); }
      }, 2000);

    } catch (err) {
      console.error('Failed to accept call:', err);
      toast.error('Could not start call — check microphone/camera permissions');
      try {
        await axios.post(`${API}/calls/${incomingCall.call_id}/decline`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) { console.debug('Call listener error:', e.message); }
      cleanupCall();
    }
  };

  const handleDecline = async () => {
    if (!incomingCall) return;
    stopRingtone();
    dismissedCallsRef.current.add(incomingCall.call_id);
    try {
      await axios.post(`${API}/calls/${incomingCall.call_id}/decline`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) { console.debug('Call listener error:', err.message); }
    setPhase('idle');
    setIncomingCall(null);
  };

  const endCall = async () => {
    if (incomingCall) {
      try {
        await axios.post(`${API}/calls/${incomingCall.call_id}/end`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) { console.debug('Call listener error:', err.message); }
    }
    cleanupCall();
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOff(!videoTrack.enabled);
      }
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // ── Ringing UI ──
  if (phase === 'ringing' && incomingCall) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center" data-testid="incoming-call-modal">
        <div className="bg-white rounded-3xl p-8 max-w-sm w-full mx-4 text-center shadow-2xl animate-in fade-in zoom-in duration-300">
          <div className="mb-6">
            <div className="relative inline-block">
              <Avatar className="w-24 h-24 border-4 border-coral animate-pulse">
                <AvatarImage src={incomingCall.caller?.profile_photo} />
                <AvatarFallback className="bg-coral text-white text-3xl">
                  {incomingCall.caller?.name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center border-2 border-white">
                <Phone className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-bold mb-1">{incomingCall.caller?.name}</h2>
          <p className="text-gray-500 mb-8">
            Incoming {incomingCall.call_type === 'video' ? 'video' : 'voice'} call...
          </p>

          <div className="flex items-center justify-center space-x-8">
            <button
              data-testid="decline-call-btn"
              onClick={handleDecline}
              className="p-5 rounded-full bg-red-500 hover:bg-red-600 transition-all shadow-lg hover:shadow-red-200"
            >
              <PhoneOff className="w-7 h-7 text-white" />
            </button>

            <button
              data-testid="accept-call-btn"
              onClick={handleAccept}
              className="p-5 rounded-full bg-green-500 hover:bg-green-600 transition-all shadow-lg hover:shadow-green-200"
            >
              <Phone className="w-7 h-7 text-white" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── In-Call UI ──
  if (phase === 'in-call' && incomingCall) {
    const isVideo = incomingCall.call_type === 'video';

    return (
      <div className="fixed inset-0 z-[100] bg-black flex items-center justify-center" data-testid="active-call-view">
        <div className="relative w-full h-full max-w-4xl max-h-[90vh] bg-gray-900 rounded-2xl overflow-hidden">
          {/* Remote stream */}
          <div className="absolute inset-0">
            {isVideo ? (
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                <audio ref={remoteVideoRef} autoPlay />
                <div className="text-center">
                  <Avatar className="w-32 h-32 mx-auto mb-4 border-4 border-white">
                    <AvatarImage src={incomingCall.caller?.profile_photo} />
                    <AvatarFallback className="bg-coral text-white text-4xl">
                      {incomingCall.caller?.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <h2 className="text-white text-2xl font-bold">{incomingCall.caller?.name}</h2>
                  <p className="text-gray-400 mt-2">{formatDuration(callDuration)}</p>
                </div>
              </div>
            )}
          </div>

          {/* Local video overlay */}
          {isVideo && (
            <div className="absolute bottom-24 right-4 w-32 h-44 rounded-xl overflow-hidden border-2 border-white shadow-lg">
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                className={`w-full h-full object-cover ${isVideoOff ? 'hidden' : ''}`}
              />
              {isVideoOff && (
                <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                  <VideoOff className="w-8 h-8 text-gray-400" />
                </div>
              )}
            </div>
          )}

          {/* Controls bar */}
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black to-transparent">
            {isVideo && (
              <p className="text-center text-white text-sm mb-4">{formatDuration(callDuration)}</p>
            )}
            <div className="flex items-center justify-center space-x-4">
              <button
                data-testid="mute-call-btn"
                onClick={toggleMute}
                className={`p-4 rounded-full ${isMuted ? 'bg-red-500' : 'bg-gray-700 hover:bg-gray-600'} transition-colors`}
              >
                {isMuted ? <MicOff className="w-6 h-6 text-white" /> : <Mic className="w-6 h-6 text-white" />}
              </button>

              <button
                data-testid="end-call-btn"
                onClick={endCall}
                className="p-5 rounded-full bg-red-600 hover:bg-red-700 transition-colors"
              >
                <PhoneOff className="w-8 h-8 text-white" />
              </button>

              {isVideo && (
                <button
                  data-testid="toggle-video-btn"
                  onClick={toggleVideo}
                  className={`p-4 rounded-full ${isVideoOff ? 'bg-red-500' : 'bg-gray-700 hover:bg-gray-600'} transition-colors`}
                >
                  {isVideoOff ? <VideoOff className="w-6 h-6 text-white" /> : <Video className="w-6 h-6 text-white" />}
                </button>
              )}
            </div>
          </div>

          <button
            onClick={endCall}
            className="absolute top-4 right-4 p-2 rounded-full bg-black bg-opacity-50 hover:bg-opacity-70 transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default IncomingCallListener;
